import { ROLE_LABEL_STATUSES } from "../../../constants/role-labels.ts"
import {
  type ActionRecord,
  type ColumnKey,
  type SectionRecord
} from "../types.ts"
import type {
  AnyPermissionKey,
  PermissionDisplayMap,
  PermissionFeatures,
  PermissionLabel
} from "../types/permissions.types.ts"

export const getTotalCounts = (subKeys: SectionRecord) => {
  const allActions = Object.values(subKeys).flatMap((actions) =>
    Object.values(actions)
  )
  return {
    enabled: allActions.filter(Boolean).length,
    total: allActions.length
  }
}

export const getDisplayEntry = (
  displayMap: PermissionDisplayMap | null | undefined,
  key: string
) => displayMap?.[key as AnyPermissionKey]

export const getSectionTitle = (
  sectionKey: keyof PermissionFeatures,
  displayMap?: PermissionDisplayMap | null
) => displayMap?.[sectionKey]?.name ?? sectionKey.replace(/_/g, " ")

const COLUMN_KEYS: ColumnKey[] = [
  "full_access",
  "create",
  "view",
  "delete",
  "edit"
]

const splitColumnSuffix = (
  action: string
): { entity: string; col: ColumnKey } | null => {
  for (const col of COLUMN_KEYS) {
    if (action === col) return { entity: "", col }
    if (action.endsWith(`_${col}`))
      return { entity: action.slice(0, -(col.length + 1)), col }
  }
  return null
}

// A row's entity is the shortest prefix any of its column-suffixed rights
// carries: `subcontractor` beside `subcontractor_tax` (from `_tax_view`).
const getRowEntity = (actions: ActionRecord): string | null =>
  Object.keys(actions).reduce<string | null>((shortest, action) => {
    const split = splitColumnSuffix(action)
    if (!split) return shortest
    return shortest === null || split.entity.length < shortest.length
      ? split.entity
      : shortest
  }, null)

// Only the row's own CRUD right (`<entity>_<col>`) is a grid column. A section
// right that merely ends the same way (`subcontractor_tax_view`) is an extra.
export const getColKey = (
  action: string,
  rowActions: ActionRecord
): ColumnKey | null => {
  const split = splitColumnSuffix(action)
  if (!split) return null
  return split.entity === getRowEntity(rowActions) ? split.col : null
}

export const getActionExtras = (actions: ActionRecord) =>
  Object.entries(actions).filter(
    ([action]) => getColKey(action, actions) === null
  )

export const getFullAccessKey = (actions: ActionRecord): string | undefined =>
  Object.keys(actions).find((key) => getColKey(key, actions) === "full_access")

/**
 * Synchronizes full_access with individual permissions (create, view, delete, edit)
 * @param actions - Current actions object for a specific permission
 * @param changedKey - The key that was changed
 * @param newValue - The new value for the changed key
 * @returns Updated actions object with synchronized full_access
 */
export const syncFullAccess = (
  actions: ActionRecord,
  changedKey: string,
  newValue: boolean
): ActionRecord => {
  const changedColKey = getColKey(changedKey, actions)
  const updatedActions = { ...actions, [changedKey]: newValue }

  // Find keys for each permission type
  const fullAccessKey = Object.keys(actions).find(
    (key) => getColKey(key, actions) === "full_access"
  )
  const createKey = Object.keys(actions).find(
    (key) => getColKey(key, actions) === "create"
  )
  const viewKey = Object.keys(actions).find(
    (key) => getColKey(key, actions) === "view"
  )
  const deleteKey = Object.keys(actions).find(
    (key) => getColKey(key, actions) === "delete"
  )
  const editKey = Object.keys(actions).find(
    (key) => getColKey(key, actions) === "edit"
  )

  // If full_access is being turned ON, enable all other permissions
  if (changedColKey === "full_access" && newValue) {
    if (createKey) updatedActions[createKey] = true
    if (viewKey) updatedActions[viewKey] = true
    if (deleteKey) updatedActions[deleteKey] = true
    if (editKey) updatedActions[editKey] = true
  }
  // If full_access is being turned OFF, disable all other permissions
  else if (changedColKey === "full_access" && !newValue) {
    if (createKey) updatedActions[createKey] = false
    if (viewKey) updatedActions[viewKey] = false
    if (deleteKey) updatedActions[deleteKey] = false
    if (editKey) updatedActions[editKey] = false
  }
  // If any individual permission is being changed, update full_access accordingly
  else if (changedColKey !== "full_access" && fullAccessKey) {
    // Check if all individual permissions are enabled
    const allEnabled =
      (!createKey || updatedActions[createKey] === true) &&
      (!viewKey || updatedActions[viewKey] === true) &&
      (!deleteKey || updatedActions[deleteKey] === true) &&
      (!editKey || updatedActions[editKey] === true)

    updatedActions[fullAccessKey] = allEnabled
  }

  return updatedActions
}

// PermissionLabel.name is max_length=100 on the model, and the duplicate flow
// bypasses the Add/Rename modal's own 50-char rule.
const MAX_LABEL_NAME_LENGTH = 100

export const getDuplicateName = (
  name: string,
  labels: PermissionLabel[]
): string => {
  const taken = new Set(
    labels.map((l) => l?.name?.toLowerCase()).filter(Boolean)
  )

  for (let attempt = 1; ; attempt++) {
    const suffix = attempt === 1 ? " (Copy)" : ` (Copy ${attempt})`
    const base = name.slice(0, MAX_LABEL_NAME_LENGTH - suffix.length)
    const candidate = `${base}${suffix}`

    if (!taken.has(candidate.toLowerCase())) return candidate
  }
}

export const getDuplicateCode = (
  code: string,
  labels: PermissionLabel[]
): string => {
  const baseCode = code.replace(/_\d+$/, "")

  const matchingCodes = labels
    .map((l) => l.code)
    .filter((c) => c === baseCode || new RegExp(`^${baseCode}_\\d+$`).test(c))

  const maxNumber = matchingCodes.reduce((max, current) => {
    const match = current.match(/_(\d+)$/)

    if (!match) return Math.max(max, 0)

    return Math.max(max, Number(match[1]))
  }, 0)

  return `${baseCode}_${maxNumber + 1}`
}

export const isGccOwnerRole = (label: Pick<PermissionLabel, "code"> | null) =>
  label?.code === ROLE_LABEL_STATUSES.OWNER

// The protected GCC Owner role is shown to the owner alone, and first.
export const getVisibleRoleLabels = <T extends Pick<PermissionLabel, "code">>(
  labels: T[],
  canSeeOwnerRole: boolean
): T[] => {
  const owner = labels.filter(isGccOwnerRole)
  const others = labels.filter((label) => !isGccOwnerRole(label))
  return canSeeOwnerRole ? [...owner, ...others] : others
}
